// stereo-core: rosette (azimuth frequency) diagrams.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { DEG } from "./vec.js";
import { wrap360, dipDirToStrikeRHR } from "./orient.js";

export type RosetteMode = "strike" | "dipDir";
export interface RosetteBin { from: number; to: number; value: number; count: number }
export interface Rosette {
  mode: RosetteMode; binSize: number; bins: RosetteBin[]; // strike: bins cover 0–180 and are drawn twice
  total: number; n: number; max: number;
  mean: number | null;      // vector mean (dip direction) or axial mean (strike, doubled angles), degrees
  meanLength: number;       // resultant length 0–1 (concentration)
}

// [ROS-01] [ROS-02] [ROS-03]
/**
 * Strike is axial (a strike of 30° is the same line as 210°), so it is binned over
 * 0–180° and each petal is mirrored; its mean uses doubled angles. Dip direction is
 * directional and binned over 0–360°. Only planes with dipMin ≤ dip ≤ dipMax count.
 */
export function rosette(planes: { dip: number; dipDir: number }[], o: { mode?: RosetteMode; binSize?: number; dipMin?: number; dipMax?: number; weights?: number[] } = {}): Rosette {
  const mode = o.mode ?? "strike", bin = Math.max(1, o.binSize ?? 10), lo = o.dipMin ?? 0, hi = o.dipMax ?? 90;
  const span = mode === "strike" ? 180 : 360, nb = Math.ceil(span / bin);
  const bins: RosetteBin[] = Array.from({ length: nb }, (_, k) => ({ from: k * bin, to: Math.min(span, (k + 1) * bin), value: 0, count: 0 }));
  let total = 0, n = 0, sx = 0, sy = 0;
  planes.forEach((p, i) => {
    if (p.dip < lo - 1e-9 || p.dip > hi + 1e-9) return;
    const w = o.weights ? o.weights[i] : 1;
    const az = mode === "strike" ? dipDirToStrikeRHR(p.dipDir) % 180 : wrap360(p.dipDir);
    const k = Math.min(nb - 1, Math.floor(az / bin));
    bins[k].value += w; bins[k].count++; total += w; n++;
    const a = (mode === "strike" ? 2 * az : az) * DEG; sx += w * Math.sin(a); sy += w * Math.cos(a);
  });
  const R = total > 0 ? Math.hypot(sx, sy) / total : 0;
  let mean: number | null = null;
  if (total > 0 && R > 1e-9) { const m = wrap360(Math.atan2(sx, sy) / DEG); mean = mode === "strike" ? m / 2 : m; }
  return { mode, binSize: bin, bins, total, n, max: Math.max(0, ...bins.map((b) => b.value)), mean, meanLength: R };
}
